import { useEffect, useMemo, useState } from "react";
import "./App.css";

function readStoppedFromUrl() {
  try {
    return new URLSearchParams(window.location.search).get("stopped") === "1";
  } catch {
    return false;
  }
}

export default function App() {
  const gameUrl = process.env.REACT_APP_GAME_URL || "https://game.uski.fi";
  const startEndpoint = process.env.REACT_APP_START_API || "";
  const statusEndpoint = process.env.REACT_APP_STATUS_API || "";
  const vmStartUrl =
    process.env.REACT_APP_VM_START_URL ||
    "https://portal.azure.com/#blade/HubsExtension/BrowseResource/resourceType/Microsoft.Compute%2FVirtualMachines";
  const hostPasscode = (process.env.REACT_APP_HOST_PASSCODE || "").trim();
  const launcherApiKey = (process.env.REACT_APP_LAUNCHER_KEY || "").trim();
  const hostAuthConfigured = hostPasscode.length > 0;

  const [mode, setMode] = useState("home");
  const [passInput, setPassInput] = useState("");
  const [authError, setAuthError] = useState("");
  const [status, setStatus] = useState("checking");
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");

  const showStoppedBanner = useMemo(() => readStoppedFromUrl(), []);

  useEffect(() => {
    if (!showStoppedBanner) return;
    const url = new URL(window.location.href);
    url.searchParams.delete("stopped");
    window.history.replaceState({}, "", url.pathname + (url.search ? url.search : ""));
  }, [showStoppedBanner]);

  async function checkServerStatus() {
    setStatus((s) => (s === "starting" ? "starting" : "checking"));
    try {
      if (statusEndpoint) {
        const res = await fetch(statusEndpoint, {
          method: "GET",
          headers: {
            "x-launcher-key": launcherApiKey,
          },
          cache: "no-store",
        });
        if (!res.ok) {
          throw new Error(`Status check failed (${res.status})`);
        }
        const data = await res.json().catch(() => ({}));
        const ps = String(data.powerState || "").toLowerCase();
        const running = ps === "running";
        if (running) {
          setStatus("running");
          setNote("Game server is online.");
          return "running";
        }
        const transitional =
          ps === "starting" || ps === "stopping" || ps === "deallocating";
        if (transitional) {
          setStatus("starting");
          setNote(`Server state: ${ps}`);
          return "starting";
        }
        setStatus("stopped");
        setNote("");
        return "stopped";
      }

      await fetch(`${gameUrl}/`, { method: "GET", cache: "no-store", mode: "no-cors" });
      setStatus("running");
      setNote("Game server is online.");
      return "running";
    } catch {
      setStatus((s) => (s === "starting" ? "starting" : "stopped"));
      setNote("");
      return "stopped";
    }
  }

  useEffect(() => {
    checkServerStatus().then((s) => {
      if (s !== "running") setStatus("stopped");
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const canJoin = status === "running";
  const statusText = useMemo(() => {
    if (status === "running") return "Running";
    if (status === "starting") return "Starting";
    if (status === "checking") return "Checking";
    if (status === "error") return "Error";
    return "Stopped";
  }, [status]);

  function openGame(role) {
    const withRole = role ? `${gameUrl}/?role=${encodeURIComponent(role)}` : `${gameUrl}/`;
    window.open(withRole, "_blank", "noopener,noreferrer");
  }

  function onHostClick() {
    setMode("host_auth");
    setAuthError("");
    setPassInput("");
    setNote("");
  }

  function onHostUnlock(e) {
    e.preventDefault();
    if (!hostAuthConfigured) {
      setAuthError(
        "Host passcode not configured in build environment (REACT_APP_HOST_PASSCODE)."
      );
      return;
    }
    if (passInput.trim() !== hostPasscode) {
      setAuthError("Authentication failed.");
      return;
    }
    setAuthError("");
    setMode("host_panel");
  }

  async function startServer() {
    setBusy(true);
    setNote("Start request initiated...");
    setStatus("starting");
    try {
      if (!startEndpoint) {
        window.open(vmStartUrl, "_blank", "noopener,noreferrer");
        setNote(
          "Opened Azure Portal. Start your VM there, then return here and click Re-check status."
        );
        return;
      }
      const res = await fetch(startEndpoint, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-launcher-key": launcherApiKey,
        },
        body: JSON.stringify({ action: "start" }),
      });
      if (!res.ok) {
        throw new Error(`Start failed (${res.status})`);
      }
      setNote("Server start triggered. This can take 1-3 minutes.");
    } catch (err) {
      setStatus("error");
      setNote(
        `${err.message}. If backend start API is not ready yet, start the VM manually and re-check status.`
      );
    } finally {
      setBusy(false);
      setTimeout(() => {
        checkServerStatus();
      }, 3000);
    }
  }

  return (
    <div className="launcher-app">
      {showStoppedBanner && (
        <div className="launcher-banner" role="status">
          Thank you for the game today. Game server has been stopped. See you soon again!
        </div>
      )}

      <div className="launcher-inner">
        <h1>Huutopussi</h1>
        <p className="lead">
          Choose whether to host a new game or join an existing one.
        </p>

        <div className="statusRow">
          <span className={`badge badge--${status}`}>Server: {statusText}</span>
          <button
            type="button"
            className="btn btn--subtle"
            onClick={checkServerStatus}
            disabled={busy}
          >
            Re-check status
          </button>
        </div>

        {mode === "home" && (
          <div className="card">
            <div className="buttonRow">
              <button type="button" className="btn btn--primary" onClick={onHostClick}>
                Host a game
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={!canJoin}
                onClick={() => openGame("join")}
              >
                Join a game
              </button>
            </div>
            {!canJoin && (
              <p className="hint">
                Join is disabled while the game server is offline.
              </p>
            )}
          </div>
        )}

        {mode === "host_auth" && (
          <form className="card" onSubmit={onHostUnlock}>
            <h2>Host authentication</h2>
            <p className="hint">Enter host passcode to access server controls.</p>
            {!hostAuthConfigured && (
              <p className="error">
                Host passcode is missing from launcher build config.
              </p>
            )}
            <input
              type="password"
              className="input"
              value={passInput}
              onChange={(e) => setPassInput(e.target.value)}
              placeholder="Host passcode"
              autoFocus
            />
            {authError && <p className="error">{authError}</p>}
            <div className="buttonRow">
              <button type="submit" className="btn btn--primary" disabled={!hostAuthConfigured}>
                Authenticate
              </button>
              <button type="button" className="btn btn--subtle" onClick={() => setMode("home")}>
                Back
              </button>
            </div>
          </form>
        )}

        {mode === "host_panel" && (
          <div className="card">
            <h2>Host controls</h2>
            <p className="hint">
              Start the server if needed, then join the game lobby as host.
            </p>
            {!startEndpoint && (
              <p className="hint">
                Manual mode: this opens Azure Portal where you start the VM. Once the VM is
                running, click Re-check status.
              </p>
            )}
            <div className="buttonRow">
              <button type="button" className="btn btn--primary" onClick={startServer} disabled={busy}>
                Start game server and launch new game
              </button>
              <button
                type="button"
                className="btn btn--secondary"
                disabled={!canJoin || busy}
                onClick={() => openGame("host")}
              >
                Join as host
              </button>
              <button type="button" className="btn btn--subtle" onClick={() => setMode("home")}>
                Back
              </button>
            </div>
          </div>
        )}

        {note && <p className="note">{note}</p>}

        <p className="footnote">
          Build config: start API {startEndpoint ? "configured" : "missing"}, status API{" "}
          {statusEndpoint ? "configured" : "missing"}.
        </p>
      </div>
    </div>
  );
}
